// Offline service for PWA functionality
// Handles offline mode, sync queue, and network status

export class OfflineService {
  private listeners: ((online: boolean) => void)[] = [];
  private isOnline: boolean = navigator.onLine;

  constructor() {
    // Listen to online/offline events
    window.addEventListener('online', () => this.handleStatusChange(true));
    window.addEventListener('offline', () => this.handleStatusChange(false));
  }

  private handleStatusChange(online: boolean) {
    this.isOnline = online;
    this.listeners.forEach(listener => listener(online));
  }

  // Subscribe to status changes
  onStatusChange(callback: (online: boolean) => void) {
    this.listeners.push(callback);
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  // Get current online status
  getStatus(): boolean {
    return this.isOnline;
  }

  // Queue data for later sync
  async queueForSync(data: any) {
    try {
      const queue = this.getQueue();
      queue.push(data);
      localStorage.setItem('offline-queue', JSON.stringify(queue));
      return true;
    } catch (error) {
      console.error('Failed to queue data:', error);
      return false;
    }
  }

  // Get queued items
  getQueue(): any[] {
    try {
      const queue = localStorage.getItem('offline-queue');
      return queue ? JSON.parse(queue) : [];
    } catch (error) {
      console.error('Failed to get queue:', error);
      return [];
    }
  }

  // Clear queue after successful sync
  clearQueue() {
    localStorage.removeItem('offline-queue');
  }

  // Sync queued data
  async syncQueue() {
    if (!this.isOnline) {
      return false;
    }

    const queue = this.getQueue();
    if (queue.length === 0) {
      return true;
    }

    try {
      // Process queue items (would make API calls here)
      console.log('Syncing queue:', queue);
      this.clearQueue();
      return true;
    } catch (error) {
      console.error('Failed to sync queue:', error);
      return false;
    }
  }
}

export const offlineService = new OfflineService();
